import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

export default function Register() {

  // Form state (name, email, password)
  const [form, setForm] = useState({ name: '', email: '', password: '' });

  // UI states
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth(); // reuse login after register
  const navigate = useNavigate();

  // Handle register
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!form.name || !form.email || !form.password) {
      return setError('All fields are required');
    }

    if (form.name.trim().length === 0) {
      return setError('Name cannot be empty');
    }

    if (form.password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    try {
      setLoading(true);

      // Call backend register API
      const res = await axios.post('/auth/register', form);

      // Auto login after successful signup
      login(res.data.user, res.data.token);

      // Redirect to dashboard
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>

        {/* App logo */}
        <div style={styles.logoRow}>
          <Logo size={48} />
        </div>

        <h2 style={styles.title}>Create Account</h2>
        <p style={styles.subtitle}>Start managing your tasks</p>

        {/* Error message */}
        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>

          {/* Name input */}
          <div style={styles.field}>
            <label style={styles.label}>Name</label>
            <input
              style={styles.input}
              type="text"
              placeholder="Your full name"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              autoComplete="name"
            />
          </div>

          {/* Email input */}
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              autoComplete="email"
            />
          </div>

          {/* Password input */}
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              style={styles.input}
              type="password"
              placeholder="Min 6 characters"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password"
            />
          </div>

          {/* Submit */}
          <button style={styles.button} disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        {/* Redirect to login */}
        <p style={styles.link}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>

      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #ede9fe 0%, #f5f3ff 50%, #eff6ff 100%)'
  },
  card: {
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(79, 70, 229, 0.12)',
    width: '100%',
    maxWidth: '400px'
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1.25rem'
  },
  title: {
    margin: 0,
    fontSize: '1.5rem',
    color: '#1a1a1a',
    textAlign: 'center',
    fontWeight: 700
  },
  subtitle: {
    color: '#6b7280',
    marginTop: '0.25rem',
    marginBottom: '1.5rem',
    textAlign: 'center',
    fontSize: '0.875rem'
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '0.75rem',
    borderRadius: '6px',
    marginBottom: '1rem',
    fontSize: '0.875rem'
  },
  field: { marginBottom: '1rem' },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500,
    color: '#374151',
    fontSize: '0.875rem'
  },
  input: {
    width: '100%',
    padding: '0.625rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s'
  },
  button: {
    width: '100%',
    padding: '0.75rem',
    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem'
  },
  link: {
    textAlign: 'center',
    marginTop: '1rem',
    color: '#6b7280',
    fontSize: '0.875rem'
  }
};